import { mapCarrierIdToType } from '@/utils/carrierMapper'
import { ADMIN_EFP_ORDER_CARRIER_LABELS, ADMIN_EFP_ORDER_STATUS_LABELS } from '@/types/adminEfpOrders'
import type { AdminEfpOrder, AdminEfpOrderAcheteur, AdminEfpOrderVendeur } from '@/types/adminEfpOrders'

/** Classes CSS legacy (efashionparis/www/core/css/admin/style.css) */
export const ADMIN_ORDER_STATUS_CSS_CLASS: Record<number, string> = {
  0: 'admin-order-statut-miseenpanier',
  1: 'admin-order-statut-enattente',
  2: 'admin-order-statut-expediee',
  3: 'admin-order-statut-annule',
  4: 'admin-order-statut-enexpedition',
  5: 'admin-order-statut-enattente',
  6: 'admin-order-statut-prepagroupage',
  7: 'admin-order-statut-pretcolis',
  8: 'admin-order-statut-pickup',
  14: 'admin-order-statut-recu-depot',
  11: 'admin-order-statut-enattentehero',
  12: 'admin-order-statut-bloquee',
  13: 'admin-order-statut-enattente',
}

export function getAdminOrderStatusClass(statut: number | null | undefined): string {
  if (statut == null) return ''
  return ADMIN_ORDER_STATUS_CSS_CLASS[statut] ?? ''
}

export function getAdminOrderStatusLabel(statut: number | null | undefined): string {
  if (statut == null) return 'Non défini'
  return ADMIN_EFP_ORDER_STATUS_LABELS[statut] ?? `Statut ${statut}`
}

export function formatAdminDecimal(
  value: number | null | undefined,
  fallback = '—',
): string {
  if (value == null || Number.isNaN(value)) return fallback
  return value.toFixed(2)
}

export function formatAdminDate(dateString: string | null | undefined): string {
  if (!dateString || dateString === '0000-00-00 00:00:00') return 'Mise en panier'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

/** Date calendaire MySQL / ISO API → YYYY-MM-DD sans décalage fuseau. */
export function extractAdminCalendarDate(
  dateString: string | null | undefined,
): string | null {
  if (
    !dateString ||
    dateString === '0000-00-00 00:00:00' ||
    dateString === '0000-00-00'
  ) {
    return null
  }

  const str = String(dateString).trim()

  if (str.includes('T')) {
    const parsed = new Date(str)
    if (Number.isNaN(parsed.getTime())) return null
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(parsed)
  }

  const plain = str.match(/^(\d{4}-\d{2}-\d{2})/)
  return plain ? plain[1] : null
}

export function formatAdminDateShort(dateString: string | null | undefined): string {
  const part = extractAdminCalendarDate(dateString)
  if (!part) return '—'
  const [year, month, day] = part.split('-')
  return `${day}/${month}/${year}`
}

/** Valeur pour `<input type="date">` (YYYY-MM-DD) */
export function formatAdminDateInput(
  dateString: string | null | undefined,
): string {
  return extractAdminCalendarDate(dateString) ?? ''
}

export function getAdminVendeurDisplay(vendeur?: AdminEfpOrderVendeur): string {
  if (!vendeur) return 'N/A'
  return (
    (vendeur.nomBoutique && vendeur.nomSociete
      ? `${vendeur.nomBoutique} (${vendeur.nomSociete})`
      : vendeur.nomSociete || vendeur.nomBoutique) ||
    vendeur.email ||
    vendeur.nomContact ||
    `Vendeur ${vendeur.id_vendeur}`
  )
}

/**
 * Découpe le libellé vendeur "Boutique (Société)" en deux parties afin
 * d'afficher la partie entre parenthèses sur une nouvelle ligne.
 */
export function splitAdminVendeurLabel(label: string | null | undefined): {
  name: string
  paren: string
} {
  const text = (label ?? '').trim()
  const idx = text.indexOf('(')
  if (idx === -1 || !text.endsWith(')')) {
    return { name: text, paren: '' }
  }
  const name = text.slice(0, idx).trim()
  const paren = text.slice(idx).trim()
  // Si le contenu entre parenthèses est identique au nom, ne pas l'afficher.
  const parenInner = paren.replace(/^\(/, '').replace(/\)$/, '').trim()
  if (parenInner.toLowerCase() === name.toLowerCase()) {
    return { name, paren: '' }
  }
  return { name, paren }
}

export function getAdminAcheteurDisplay(acheteur?: AdminEfpOrderAcheteur): string {
  if (!acheteur) return 'N/A'
  return (
    acheteur.nomSociete ||
    acheteur.nomContact ||
    acheteur.prenomContact ||
    `Acheteur ${acheteur.id_acheteur}`
  )
}

export function getAdminAcheteurContact(acheteur?: AdminEfpOrderAcheteur): string {
  if (!acheteur) return ''
  return [acheteur.nomContact, acheteur.prenomContact].filter(Boolean).join(' ')
}

export function formatAdminPaysLivraison(
  pays?: { nom?: string | null; codePostal?: string | null } | null,
): string {
  if (!pays?.nom?.trim()) return '—'
  const codePostal = pays.codePostal?.trim()
  if(pays.nom === 'France DOM/TOM' && codePostal) {
    switch (codePostal.substring(0, 3)) {
      case '971':
        return `${pays.nom.trim()} (${codePostal}) Guadeloupe`
      case '972':
        return `${pays.nom.trim()} (${codePostal}) Martinique`
      case '973':
        return `${pays.nom.trim()} (${codePostal}) Guyane`
      case '974':
        return `${pays.nom.trim()} (${codePostal}) Réunion`
      case '975':
        return `${pays.nom.trim()} (${codePostal}) Saint-Pierre-et-Miquelon`
      case '976':
        return `${pays.nom.trim()} (${codePostal}) Mayotte`
      case '977':
        return `${pays.nom.trim()} (${codePostal}) Saint-Barthélemy`
      case '978':
        return `${pays.nom.trim()} (${codePostal}) Saint-Martin`
      case '984':
        return `${pays.nom.trim()} (${codePostal}) Terres australes et antarctiques françaises`
      case '986':
        return `${pays.nom.trim()} (${codePostal}) Wallis-et-Futuna`
      case '987':
        return `${pays.nom.trim()} (${codePostal}) Polynésie française`
      case '988':
        return `${pays.nom.trim()} (${codePostal}) Nouvelle-Calédonie`
      case '989':
        return `${pays.nom.trim()} (${codePostal}) Clipperton`
    }
  }
  if (codePostal) return `${pays.nom.trim()} (${codePostal})`
  return pays.nom.trim()
}

/** Ligne titre legacy : "Code Promotion :" si un code existe, sinon "Remise". */
export function formatAdminPromoTypeLabel(promo: {
  code?: string | null
}): string {
  return promo.code && promo.code.trim() ? 'Code Promotion :' : 'Remise'
}

export function formatAdminPromoCodeLabel(promo: {
  code: string
  montant: number
  type: string
  libelleRemise?: string | null
}): string {
  const suffix = promo.type === 'pourcentage' ? ' %' : ' €'
  const montant = (Number(promo.montant) || 0).toFixed(2)
  if (!promo.code || !promo.code.trim()) {
    const base = promo.libelleRemise?.trim()
    return base
      ? `${base} - ${montant}${suffix}`
      : `${montant}${suffix}`
  }
  return `${promo.code} - ${montant}${suffix}`
}

const ADMIN_DOM_TOM_ISO: Record<string, string> = {
  '971': 'GP',
  '972': 'MQ',
  '973': 'GF',
  '974': 'RE',
  '975': 'PM',
  '976': 'YT',
  '977': 'BL',
  '978': 'MF',
  '984': 'TF',
  '986': 'WF',
  '987': 'PF',
  '988': 'NC',
  '989': 'PF',
}

export function getAdminPaysLivraisonFlagCode(
  pays?: { code?: string | null; codePostal?: string | null } | null,
): string | null {
  const base = pays?.code?.trim().toUpperCase()
  if (!base || base.length !== 2) return null

  if (base === 'FR') {
    const codePostal = pays?.codePostal?.trim() ?? ''
    if (/^[0-9]{5}$/.test(codePostal)) {
      return ADMIN_DOM_TOM_ISO[codePostal.substring(0, 3)] ?? base
    }
  }

  return base
}

export function getAdminFlagImgUrl(code: string): string {
  return `https://flagcdn.com/24x18/${code.toLowerCase()}.png`
}

export function iso2ToFlagEmoji(code?: string | null): string {
  if (!code || code.length !== 2) return ''
  const up = code.toUpperCase()
  return (
    String.fromCodePoint(up.charCodeAt(0) - 65 + 0x1f1e6) +
    String.fromCodePoint(up.charCodeAt(1) - 65 + 0x1f1e6)
  )
}

/**
 * Couleur du transporteur, alignée sur le legacy GetLivraisonColor
 * (efashionparis/www/core/php/application/commande2.class.php).
 * Le legacy fait un switch sur la valeur brute de id_livraison (seuls
 * 1/3/9/10/17/19 ont une couleur dédiée) ; toutes les autres valeurs (dont
 * les id_livraison modernes type 2918/2932) retombent sur le défaut orange.
 */
const ADMIN_LIVRAISON_COLOR_BY_ID: Record<string, string> = {
  '19': '#76561f',
  '10': '#395fdb',
  '1': '#db1d4e',
  '9': '#009050',
  '17': '#017ec2',
  '3': '#017ec2',
}

export function getAdminLivraisonColor(
  idLivraison: number | string | null | undefined,
): string {
  if (!idLivraison) return '#e67e22'
  return ADMIN_LIVRAISON_COLOR_BY_ID[idLivraison.toString()] ?? '#e67e22'
}

/**
 * Classe CSS de mise en évidence du Port HT par transporteur, alignée sur le
 * legacy fraisportHTML (commande2.class.php) qui enveloppe le montant dans
 * <div class="<transporteur>options"> (cf. commandes.liste.css).
 */
const ADMIN_PORT_HT_CLASS_BY_TYPE: Record<string, string> = {
  colissimo: 'colissimooptions',
  dpd: 'exapaqoptions',
  gls: 'glsoptions',
  gpx: 'gpxoptions',
  ups: 'upsoptions',
  fedex: 'fedexoptions',
  bollore: 'bolloreoptions',
  chronopost: 'chronopostoptions',
  dhl: 'dhloptions',
}

export function getAdminPortHtCarrierClass(
  idLivraison: number | string | null | undefined,
): string {
  if (!idLivraison) return ''
  if (idLivraison.toString() === '2741') return 'africaoptions'
  const type = mapCarrierIdToType(idLivraison)
  return ADMIN_PORT_HT_CLASS_BY_TYPE[type] ?? ''
}

export function getAdminOrderCarrierLabel(order: AdminEfpOrder): string {
  if (!order.id_livraison) return '—'
  if (order.id_livraison === 2932) return 'UPS Standard ( Groupage )'
  if (order.id_livraison === 2931) return 'GLS ( Multi )'

  const type = mapCarrierIdToType(order.id_livraison)
  if (type === 'client') return 'Transporteur acheteur'
  if (order.groupage) {
    if (type === 'fedex') return 'Fedex ( Groupage )'
    if (type === 'ups') return 'UPS ( Groupage )'
    if (type === 'chronopost' && order.id_livraison === 2918) return 'Chrono Multi ( Groupage )'
  }
  return ADMIN_EFP_ORDER_CARRIER_LABELS[type] || type
}
